import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pickLandingPath, UserRole } from '../shared/roles';

export default function ProtectedRoute({ children, requireSubscription = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#C6A85B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Users still in setup (no intent, or end-user not yet onboarded) get bounced
  // to the right next step. Approved vendors + the setup pages themselves are
  // allowed through.
  const setupPaths = new Set(['/welcome', '/onboarding', '/apply', '/apply/gym', '/apply/trainer', '/apply/under-review']);
  const role = user.role ?? UserRole.USER;
  const isApprovedVendor = role === UserRole.TRAINER || role === UserRole.GYM_OWNER || role === UserRole.ADMIN;
  if (!isApprovedVendor && !setupPaths.has(location.pathname)) {
    const needsIntent = !user.role_intent;
    const needsOnboarding = user.role_intent === UserRole.USER && !user.is_onboarded;
    const needsVendorFlow = (user.role_intent === UserRole.GYM_OWNER || user.role_intent === UserRole.TRAINER);
    if (needsIntent || needsOnboarding || needsVendorFlow) {
      return <Navigate to={pickLandingPath(user)} replace />;
    }
  }

  if (requireSubscription && !user.is_subscribed) {
    return <Navigate to="/payment" replace />;
  }

  return children;
}
